import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getNotificationPrefs, setNotificationPref } from '../tareas/api';
import type { NotificationPrefs } from '../tareas/api';

const ROWS: Array<{ key: keyof NotificationPrefs; label: string; desc: string }> = [
  { key: 'notif_assignment', label: 'Hilo asignado', desc: 'Cuando alguien te asigna un hilo.' },
  { key: 'notif_comment', label: 'Comentario nuevo', desc: 'Cuando alguien comenta en un hilo en el que participas.' },
  { key: 'notif_mention', label: 'Menciones', desc: 'Cuando te mencionan con @ en un mensaje.' },
  { key: 'notif_follow', label: 'Me añaden a un hilo', desc: 'Cuando alguien te etiqueta como seguidor de un hilo.' },
  { key: 'notif_validation', label: 'Por validar', desc: 'Cuando el asignado marca como resuelto un hilo que creaste y queda pendiente de tu validación.' },
  { key: 'notif_daily_digest', label: 'Resumen diario', desc: 'Cada mañana, un correo con tus hilos y recordatorios abiertos.' },
  { key: 'notif_weekly_digest', label: 'Resumen semanal por persona', desc: 'Cada lunes, un correo con lo que te debe cada persona y lo que le debes tú.' },
];

export default function NotificationSettings() {
  const { profile } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!profile?.id) return;
    getNotificationPrefs(profile.id)
      .then(setPrefs)
      .catch(() => setError('No se pudieron cargar las preferencias.'))
      .finally(() => setLoading(false));
  }, [profile?.id]);

  const toggle = async (key: keyof NotificationPrefs) => {
    if (!profile?.id || !prefs) return;
    const next = !prefs[key];
    setPrefs({ ...prefs, [key]: next }); // optimista
    setBusyKey(key); setError('');
    try {
      await setNotificationPref(profile.id, key, next);
    } catch {
      setPrefs({ ...prefs, [key]: !next }); // revertir
      setError('No se pudo guardar el cambio.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section style={styles.card}>
      <h2 style={styles.cardTitle}>Notificaciones por email</h2>
      <p style={styles.help}>
        Elige qué avisos quieres recibir en tu correo. Los cambios se guardan al instante.
      </p>

      {loading ? (
        <p style={styles.muted}>Cargando…</p>
      ) : !prefs ? (
        <p style={styles.muted}>{error || 'No disponible.'}</p>
      ) : (
        <div style={styles.list}>
          {ROWS.map((r) => (
            <div key={r.key} style={styles.row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.label}>{r.label}</div>
                <div style={styles.desc}>{r.desc}</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={prefs[r.key]}
                disabled={busyKey === r.key}
                onClick={() => toggle(r.key)}
                style={{ ...styles.switch, ...(prefs[r.key] ? styles.switchOn : null) }}
                title={prefs[r.key] ? 'Activado' : 'Desactivado'}
              >
                <span style={{ ...styles.knob, ...(prefs[r.key] ? styles.knobOn : null) }} />
              </button>
            </div>
          ))}
          {error && <p style={styles.error}>{error}</p>}
        </div>
      )}
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 },
  help: { fontSize: 13, color: '#64748b', margin: '8px 0 16px', lineHeight: 1.5 },
  muted: { color: '#94a3b8', fontSize: 14, margin: 0 },
  list: { display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
  label: { fontSize: 14, fontWeight: 600, color: '#0f172a' },
  desc: { fontSize: 13, color: '#64748b', marginTop: 2, lineHeight: 1.4 },
  switch: {
    width: 42, height: 24, minWidth: 42, borderRadius: 999, border: 'none', cursor: 'pointer',
    background: '#cbd5e1', position: 'relative', padding: 0, transition: 'background .15s ease',
  },
  switchOn: { background: '#16a34a' },
  knob: {
    position: 'absolute', top: 3, left: 3, width: 18, height: 18, borderRadius: '50%',
    background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)', transition: 'left .15s ease',
  },
  knobOn: { left: 21 },
  error: { color: '#dc2626', fontSize: 13, marginTop: 12 },
};
